import { AsyncPipe, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  input,
  output,
  inject,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CouponService, ExcelService } from '@fitness-force/api';
import type {
  AddCouponWithClient,
  Client,
  CouponByMasterId,
  CouponEligibleClient,
} from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { STAFF_STATE } from '@fitness-force/state';
import { ToastMessageService } from '@fitness-force/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { BehaviorSubject } from 'rxjs';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { MiniSearchComponent } from '@fitness-force/common/search';

@Component({
  selector: 'coupon-eligible-clients',
  templateUrl: './coupon-eligible-clients.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToastMessageService],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    ScrollPanelModule,
    ShortNamePipe,
    AvatarModule,
    MessageModule,
    MiniSearchComponent,
    TranslateModule,
    AsyncPipe,
    TitleCasePipe,
  ],
})
export class CouponEligibleClientsComponent implements OnInit {
  private coupnService = inject(CouponService);
  private translate = inject(TranslateService);
  private excelService = inject(ExcelService);
  private toastMessageService = inject(ToastMessageService);
  private store = inject(Store);

  readonly tenantId = input(0);
  readonly couponMaster = input<CouponByMasterId>();
  readonly visible = input<boolean>(false);
  readonly clientIds = input<any[]>([]);
  localClientIds: any[] = [];
  readonly selectedClients = input(
    new BehaviorSubject<CouponEligibleClient[]>([]),
  );
  readonly closeEvent = output<any>();

  eligibleClients = new BehaviorSubject<CouponEligibleClient[]>([]);
  showDownload: boolean = false;
  showCreateProspectPopup$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  reloadList: boolean = false;
  getAccessedStaffList$ = new BehaviorSubject<any[] | any>([]);

  ngOnInit(): void {
    if (this.couponMaster() != null) {
      this.GetClients();
      this.GetStaffs();
      this.showDownload = true;
    } else this.showDownload = false;
  }

  GetClients() {
    const couponMaster = this.couponMaster();
    if (!couponMaster) return;
    this.coupnService
      .getEligibleClients(this.tenantId(), couponMaster.id)
      .subscribe({
        next: (response: any) => {
          this.selectedClients().next(response.items as CouponEligibleClient[]);
          // visible is controlled by parent component
        },
      });
  }

  AddClient(client: Client) {
    if (this.couponMaster() != null) {
      this.AddClientInCoupon(client);
    } else {
      let addClients: CouponEligibleClient[] = [];
      let addClient: CouponEligibleClient = {} as CouponEligibleClient;
      addClient.client_id = client.clt_id;
      addClient.client_name = client.clt_full_name;
      addClient.client_iamge = client.clt_image;
      addClients.push(addClient);
      this.clientIds().push(client.clt_id);
      this.selectedClients().next([
        ...this.selectedClients().getValue(),
        ...addClients,
      ]);
    }
  }

  removeClient(client: CouponEligibleClient) {
    if (this.couponMaster() != null && client.id != 0) {
      this.RemoveClientInCoupon(client);
    } else {
      let ids = this.clientIds();
      let updatedClients = this.selectedClients()
        .getValue()
        .filter((c) => c.client_id != client.client_id);
      this.eligibleClients.next(
        this.eligibleClients.getValue().filter((o) => o.id != client.client_id),
      );
      this.localClientIds = [];
      this.clientIds().push(ids.filter((o) => o != client.client_id));
      this.selectedClients().next(updatedClients);
    }
  }

  AddClientInCoupon(client: Client) {
    const couponMaster = this.couponMaster();
    if (!couponMaster) return;
    let clientIds: any[] = [];
    clientIds.push(client.clt_id);
    let addClientToCoupon: AddCouponWithClient = {} as AddCouponWithClient;
    addClientToCoupon.client_ids = clientIds;
    this.coupnService
      .AddEligibleClients(this.tenantId(), couponMaster.id, addClientToCoupon)
      .subscribe({
        next: (response: any) => {
          this.translate
            .get('ADMIN.COUPON.ERRORS.CLIENt_ADDED')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.GetClients();
          this.reloadList = true;
        },
        error: (error: any) => {
          this.translate
            .get('ADMIN.COUPON.ERRORS.CLIENt_ADDED_ERROR')
            .subscribe((translation) => {
              this.toastMessageService.showErrorMessage(translation);
            });
        },
      });
  }

  RemoveClientInCoupon(client: CouponEligibleClient) {
    const couponMaster = this.couponMaster();
    if (!couponMaster) return;
    this.coupnService
      .RemoveEligibleClients(this.tenantId(), couponMaster.id, client.id)
      .subscribe({
        next: (response: any) => {
          this.translate
            .get('ADMIN.COUPON.ERRORS.CLIENt_REMOVED')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          this.GetClients();
          this.reloadList = true;
        },
        error: (error: any) => {
          this.translate
            .get('ADMIN.COUPON.ERRORS.CLIENt_REMOVED_ERROR')
            .subscribe((translation) => {
              this.toastMessageService.showErrorMessage(translation);
            });
        },
      });
  }

  async DownloadCSV() {
    let ExcelJsonData: ExcelFileDownload[] = [];
    this.selectedClients()
      .getValue()
      .forEach((o) => {
        let data: ExcelFileDownload = {} as ExcelFileDownload;
        ((data.ClientId = o.client_id), (data.Name = o.client_name));
        data.CreatedBy = this.getStaffName(o.created_by);
        data.CreatedDate = o.created_date;
        ExcelJsonData.push(data);
      });
    this.excelService.exportAsExcelFile(ExcelJsonData, 'coupon', 'csv');
  }

  GetStaffs() {
    this.store.dispatch({ type: 'FETCH_STAFF_LIST' });
    this.store.select(STAFF_STATE).subscribe((data: any) => {
      if (data && data.staffList && data.staffList.length > 0) {
        this.getAccessedStaffList$.next(
          data.staffList.filter((o: any) => {
            return String(o.status).toLowerCase() == 'active';
          }),
        );
      }
    });
  }

  getStaffName(staffId: number) {
    return this.getAccessedStaffList$
      .getValue()
      .find((o: { id: number }) => o.id == staffId).name;
  }

  closePopup() {
    this.closeEvent.emit({
      selectedClients: this.selectedClients().getValue(),
      reload: this.reloadList,
    });
    this.closeEvent.emit({
      selectedClients: this.selectedClients().getValue(),
      reload: this.reloadList,
    });
  }

  createProspect(event: any) {
    this.showCreateProspectPopup$.next(true);
  }

  hidePopup(clientId: number) {
    this.showCreateProspectPopup$.next(false);
  }

  get clients() {
    return this.selectedClients().getValue();
  }
}

export interface ExcelFileDownload {
  ClientId: number;
  Name: string;
  CreatedBy: string;
  CreatedDate: string | Date;
  UpdatedBy: number;
  UpdatedDate: string | Date;
}
